/**
 * 06 / PROOF — the receipts section. Cars lead (most installers never see a
 * truck): the AMG GT bumper and the RAM 1500 flare, two real jobs with their
 * own pages (the flare is also the part the hero morph flattens). The Volvo FH
 * Aero that supplies the Process stills and the Craft pre-flight follows as
 * the secondary block.
 */

export const proof = {
	index: '06',
	label: 'Real work',
	heading: 'Real jobs, off real cars, cut on a real plotter.',
	intro:
		'PPF Engine is being built inside a working film-installation studio in Estonia, not a software office. Everything on this page comes off a vehicle that was actually in a bay: a sports car bumper, a pickup on aftermarket flares, a truck no library covers.',
	truck: {
		label: 'And the truck behind this page',
		heading: 'A Volvo FH Aero, eight panels, on the road.',
		body: 'It came in for stone-chip protection and no pattern library covers it. We scanned the front end panel by panel, flattened the scans in PPF Engine, cut the templates on a Graphtec and installed the film. The pre-flight above is one of its panels. Below are three more, each beside the spot on the truck it protects, exactly as they went to the plotter.'
	},
	/** The actual truck, photographed in the studio bay before patterning — the
	    job's own shop-floor record, not a render. Portrait crop on the cab. */
	photo: {
		asset: 'volvo-studio',
		caption: 'Volvo FH Aero · in the studio bay, before patterning',
		alt: 'The blue Volvo FH Globetrotter from the proof job, photographed inside the PPF studio before its front end was patterned.',
		w: 1200,
		h: 1600
	},
	/**
	 * The drag-the-seam figure: one panel's INPUT against its OUTPUT. The AMG GT
	 * front bumper: the scan with its cut line on one side, the exported cut
	 * file on the other. Same renders, same aspect, as /ppf-pattern-software.
	 */
	compare: {
		before: { asset: 'mbgt-stage-scan', label: '3D scan · cut line', w: 1500, h: 890 },
		after: { asset: 'mbgt-stage-cut', label: '2D cut file', w: 2400, h: 1424 },
		alt: 'The same Mercedes-AMG GT front bumper twice: the 3D scan with the cut line traced on it, and the flat chartreuse cut file PPF Engine exported. Drag the seam to compare.',
		caption:
			'amg gt front bumper · same job · the flat side is the export, unedited · drag the seam'
	},
	/**
	 * Each cut file ships with a photo crop of the panel it protects, so the
	 * contour reads as "that exact piece of the truck," not an abstract shape.
	 */
	patterns: [
		{
			asset: 'pat-iluvore',
			caption: 'grille trim · upper',
			alt: 'Plotter-ready cutting template for the Volvo FH upper grille trim, shown as a chartreuse contour.',
			w: 1600,
			h: 735,
			loc: {
				asset: 'loc-grille',
				alt: 'The upper front trim panel on the silver Volvo, highlighted in red.',
				w: 900,
				h: 540
			}
		},
		{
			asset: 'pat-stange',
			caption: 'bumper · lower corner',
			alt: 'Plotter-ready cutting template for the Volvo FH lower bumper corner, shown as a chartreuse contour.',
			w: 1600,
			h: 330,
			loc: {
				asset: 'loc-bumper',
				alt: 'The lower bumper corner on the silver Volvo, highlighted in red.',
				w: 900,
				h: 588
			}
		},
		{
			asset: 'pat-number',
			caption: 'number-plate surround',
			alt: 'Plotter-ready cutting template for the Volvo FH number-plate surround, shown as a chartreuse contour.',
			w: 1600,
			h: 874,
			loc: {
				asset: 'loc-plate',
				alt: 'The central number-plate surround area on the silver Volvo, highlighted in red.',
				w: 900,
				h: 514
			}
		}
	],
	/**
	 * The lead jobs, each with its own page. The customer on the flare job is
	 * named on the case study only, never here.
	 */
	more: {
		jobs: [
			{
				href: '/ppf-pattern-software#proof',
				kind: 'Sports car · full bumper',
				title: 'Mercedes-AMG GT front bumper',
				body: 'The hardest panel on the car, scanned and flattened into one piece with the headlight relief cuts in place.',
				stats: '2571 × 897 mm · 429,592 faces · p95 stretch 8.0%',
				cta: 'See the job',
				asset: 'mbgt-stage-scan',
				alt: 'A scanned Mercedes-AMG GT front bumper rendered in PPF Engine with the cut line traced along every edge.',
				w: 1500,
				h: 890
			},
			{
				href: '/case-studies/ram-1500-fender-flare',
				kind: 'Pickup · aftermarket part',
				title: 'RAM 1500 bolt-on fender flare',
				body: 'No library had the flare. One scan in, one cut file out, with all nine bolt recesses traced off the mesh.',
				stats: '1485 × 435 mm · 9 cutouts · unedited export',
				cta: 'Read the case study',
				asset: 'ram-recesses',
				alt: 'Close-up of a gloss black bolt-on fender flare on a RAM 1500 with the film installed, a clean opening around each bolt pocket.',
				w: 1200,
				h: 2115
			}
		]
	},
	readouts: [
		{ label: 'Panels patterned', value: '8' },
		{ label: 'Pattern source', value: 'our own scans' },
		{ label: 'Status', value: 'cut · installed · on the road' }
	]
} as const;
